// DEV-07：NVR / Station 的通道設定比對與「複製到其他通道」。選來源通道與目標通道 → 比對（各通道的設定表單）→
// 勾要複製的項目 → 逐路寫 desired、回讀確認；每一路各自列結果，失敗的可以重試。預設只勾常用 + 進階（技術項列出來但不預設勾，DEV-07）。
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useConfirm } from '../../components/ConfirmDialog';
import { setDesired } from '@kinio/web-sdk';
import { deviceApi, affectsStreams, type SettingItem } from '../../lib/deviceApi';
import type { VmsDevice } from '../../lib/devices';
import { copyChanges, copyable, flatItems, mismatches, stillPending, toState, type CopyChange } from './copyPlan';
import { itemLabel } from '../../lib/kinioLabel';
import { DEFAULT_TIERS, inTiers, tierOf } from './settingsTiers';

type RowState = { state: 'idle' | 'pending' | 'applied' | 'failed' | 'same'; detail?: string };

const READBACK_TRIES = 12;
const READBACK_MS = 2000;
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

export function ChannelCopy({ dev, readbackMs = READBACK_MS }: { dev: VmsDevice; readbackMs?: number }) {
  const { t } = useTranslation();
  const { ask, dialog } = useConfirm();
  const chans = dev.children.filter((c) => c.channel !== null);
  const [src, setSrc] = useState(chans[0]?.aid ?? '');
  const [targets, setTargets] = useState<Set<string>>(new Set());
  const [items, setItems] = useState<Record<string, SettingItem[]> | null>(null);
  const [keys, setKeys] = useState<Set<string>>(new Set());
  const [rows, setRows] = useState<Record<string, RowState>>({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  if (chans.length < 2) return null;
  const others = chans.filter((c) => c.aid !== src);
  const picked = others.filter((c) => targets.has(c.aid));
  const srcItems = items?.[src] ?? [];
  const candidates = srcItems.filter(copyable);
  const changesFor = (aid: string): CopyChange[] => copyChanges(srcItems, items?.[aid] ?? [], keys);

  async function compare() {
    setBusy(true);
    setErr(null);
    setRows({});
    try {
      const list = await Promise.all(
        [src, ...picked.map((c) => c.aid)].map(async (aid) => [aid, flatItems(await deviceApi.settings(dev.apiEndpoint, dev.homeId, aid))] as const),
      );
      const m = Object.fromEntries(list);
      setItems(m);
      setKeys(new Set((m[src] ?? []).filter((i) => copyable(i) && inTiers(i, new Set(DEFAULT_TIERS))).map((i) => i.key)));
    } catch (e) {
      setErr(String((e as { code?: string; message?: string }).code ?? (e as Error).message ?? e));
    } finally {
      setBusy(false);
    }
  }

  async function copyOne(aid: string) {
    const ch = changesFor(aid);
    if (!ch.length) {
      setRows((r) => ({ ...r, [aid]: { state: 'same' } }));
      return;
    }
    setRows((r) => ({ ...r, [aid]: { state: 'pending' } }));
    try {
      await setDesired(dev.apiEndpoint, dev.homeId, aid, toState(ch), 'vms-web');
      // 等設備 ack（key 不在 desired）；之後跟寫入值不同 = 設備正規化或拒收
      let waiting: string[] = ch.map((c) => c.key);
      let bad: string[] = [];
      for (let i = 0; i < READBACK_TRIES && waiting.length; i++) {
        await sleep(readbackMs);
        const now = flatItems(await deviceApi.settings(dev.apiEndpoint, dev.homeId, aid));
        waiting = stillPending(ch, now);
        if (!waiting.length) {
          bad = mismatches(ch, now);
          setItems((m) => ({ ...(m ?? {}), [aid]: now }));
        }
      }
      setRows((r) => ({
        ...r,
        [aid]: waiting.length
          ? { state: 'failed', detail: t('dset.timeout') }
          : bad.length
            ? { state: 'failed', detail: t('chcopy.not_applied', { list: bad.join(', ') }) }
            : { state: 'applied', detail: t('chcopy.applied_n', { n: ch.length }) },
      }));
    } catch (e) {
      setRows((r) => ({ ...r, [aid]: { state: 'failed', detail: String((e as { code?: string; message?: string }).code ?? (e as Error).message ?? e) } }));
    }
  }

  async function copyAll(only?: string[]) {
    const list = (only ?? picked.map((c) => c.aid)).filter((aid) => items?.[aid]);
    if (list.some((aid) => changesFor(aid).some((c) => affectsStreams(c.key))) && !(await ask({ title: t('dset.confirm_streams'), action: t('common.action_change') }))) return;
    setBusy(true);
    for (const aid of list) await copyOne(aid); // 一路一路來：同一台 parent 同時收一堆 desired 容易互相蓋
    setBusy(false);
  }

  const failed = Object.entries(rows)
    .filter(([, r]) => r.state === 'failed')
    .map(([aid]) => aid);
  return (
    <section className="card" aria-label={t('chcopy.title')} data-testid="channel-copy">
      <h2 className="section-title">{t('chcopy.title')}</h2>
      <p className="muted small">{t('chcopy.help')}</p>
      <div className="toolbar">
        <label className="field">
          <span>{t('chcopy.source')}</span>
          <select
            className="input"
            value={src}
            aria-label={t('chcopy.source')}
            onChange={(e) => {
              setSrc(e.target.value);
              setItems(null);
              setRows({});
              setTargets((s) => new Set([...s].filter((a) => a !== e.target.value)));
            }}
          >
            {chans.map((c) => (
              <option key={c.aid} value={c.aid}>
                {c.name}
              </option>
            ))}
          </select>
        </label>
        <button className="btn btn-sm" onClick={() => setTargets(new Set(others.map((c) => c.aid)))}>
          {t('chcopy.all')}
        </button>
      </div>
      <fieldset className="field">
        <legend>{t('chcopy.targets')}</legend>
        <div className="check-grid">
          {others.map((c) => (
            <label key={c.aid} className="check">
              <input
                type="checkbox"
                checked={targets.has(c.aid)}
                onChange={() => {
                  setItems(null);
                  setTargets((s) => {
                    const n = new Set(s);
                    if (n.has(c.aid)) n.delete(c.aid);
                    else n.add(c.aid);
                    return n;
                  });
                }}
              />
              {c.name}
            </label>
          ))}
        </div>
      </fieldset>
      <div className="toolbar">
        <button className="btn" disabled={busy || picked.length === 0} onClick={() => void compare()}>
          {t('chcopy.compare')}
        </button>
      </div>
      {err && <p className="error">{err}</p>}
      {items && (
        <>
          {candidates.length === 0 ? (
            <p className="muted">{t('chcopy.nothing')}</p>
          ) : (
            <fieldset className="field">
              <legend>{t('chcopy.items')}</legend>
              <div className="check-grid">
                {candidates.map((i) => (
                  <label key={i.key} className="check" title={i.key} data-copy-item={i.key} data-tier={tierOf(i)}>
                    <input
                      type="checkbox"
                      checked={keys.has(i.key)}
                      onChange={() =>
                        setKeys((s) => {
                          const n = new Set(s);
                          if (n.has(i.key)) n.delete(i.key);
                          else n.add(i.key);
                          return n;
                        })
                      }
                    />
                    {itemLabel(i)}
                    {tierOf(i) === 'technical' && <span className="badge">{t('tier.technical')}</span>}
                  </label>
                ))}
              </div>
            </fieldset>
          )}
          <table className="grid">
            <thead>
              <tr>
                <th>{t('chcopy.col_channel')}</th>
                <th>{t('chcopy.col_diff')}</th>
                <th>{t('chcopy.col_result')}</th>
              </tr>
            </thead>
            <tbody>
              {picked.map((c) => {
                const r = rows[c.aid];
                const ch = changesFor(c.aid);
                return (
                  <tr key={c.aid} data-copy-target={c.aid} data-state={r?.state ?? 'idle'}>
                    <td>{c.name}</td>
                    <td title={ch.map((x) => x.label).join('\n')}>{t('chcopy.diff_n', { n: ch.length })}</td>
                    <td>
                      {!r || r.state === 'idle' ? (
                        '—'
                      ) : r.state === 'pending' ? (
                        t('common.saving')
                      ) : r.state === 'same' ? (
                        t('chcopy.same')
                      ) : r.state === 'applied' ? (
                        `✓ ${r.detail}`
                      ) : (
                        <span className="error">{r.detail}</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className="toolbar">
            <button
              className="btn btn-primary"
              disabled={busy || keys.size === 0 || picked.every((c) => changesFor(c.aid).length === 0)}
              onClick={() => void copyAll()}
            >
              {t('chcopy.copy')}
            </button>
            {failed.length > 0 && (
              <button className="btn" disabled={busy} onClick={() => void copyAll(failed)}>
                {t('chcopy.retry', { n: failed.length })}
              </button>
            )}
          </div>
        </>
      )}
      {dialog}
    </section>
  );
}
